import "./style.scss";

import FilterList from "@mui/icons-material/FilterList";
import { FormControlLabel, Popover, Radio } from "@mui/material";
import FilterInput from "components/shared/FilterInput";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledCheckbox from "components/shared/StyledCheckbox";
import type { TTags } from "components/Title";
import { tagsColors } from "constants/colors";
import { SmallBody, SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import type { Dispatch, SetStateAction } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBrand } from "store/brandStore";

import variables from "../../../../assets/styles/_variables.module.scss";

interface IMultipleFilters {
    tags: TTags[] | null;
    chosenTags: string[];
    setChosenTags: Dispatch<SetStateAction<string[]>>;
    usesBrands: boolean | undefined;
    chosenBrand: string;
    setChosenBrand: Dispatch<SetStateAction<string>>;
    brands: IBrand[] | undefined;
    companyName: string | undefined;
    showHasTermFilter?: boolean;
    withoutTermOnly?: boolean;
    setWithoutTermOnly?: (value: boolean) => void;
}

const MultipleFilters = ({
    tags,
    chosenTags,
    setChosenTags,
    usesBrands,
    chosenBrand,
    setChosenBrand,
    brands,
    companyName,
    showHasTermFilter = false,
    withoutTermOnly = false,
    setWithoutTermOnly,
}: IMultipleFilters) => {
    const [t] = useTranslation();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [searchTagQuery, setSearchTagQuery] = useState("");
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleTagSelection = (tag: TTags) => {
        if (chosenTags.includes(tag.name)) {
            setChosenTags((prevTags) => prevTags.filter((item) => item !== tag.name));
        } else {
            setChosenTags([...chosenTags, tag.name]);
        }
    };

    const handleBrandSelection = (brand: IBrand) => {
        if (chosenBrand === brand.name) {
            setChosenBrand("");
        } else {
            setChosenBrand(brand.name);
        }
    };

    const handleReset = (type: "tags" | "brands" | "hasTerm") => {
        if (type === "tags") setChosenTags([]);
        else if (type === "brands") setChosenBrand("");
        else setWithoutTermOnly?.(false);
    };

    const filteredTags = useMemo(() => {
        if (searchTagQuery)
            return tags?.filter((tag) => tag.name.toLowerCase().includes(searchTagQuery.toLowerCase())) ?? [];
        else return tags;
    }, [searchTagQuery, tags]);

    const { paginatedData: paginatedTags, paginatedElRef } = useInfiniteScroll(filteredTags, !!anchorEl);

    return (
        <div className="multiple-filters">
            <button
                className={`multiple-filter-btn ${
                    chosenBrand !== "" || chosenTags.length > 0 || withoutTermOnly ? "active" : ""
                }`}
                onClick={handleClick}
            >
                <FilterList />
            </button>
            <Popover
                className="multiple-filter-popover"
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                elevation={0}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <div className="multiple-filter-container">
                    <div className="filter-section">
                        <div className="filter-header">
                            <SmallBodyHighlight style={{ color: variables["secondary-grey"] }}>
                                {t("Filters.FilterByTag")} ({tags?.length})
                            </SmallBodyHighlight>
                            <TinyBody color="info" className="filter-reset" onClick={() => handleReset("tags")}>
                                {t("Filters.Reset")}
                            </TinyBody>
                        </div>
                        {tags && tags?.length > 20 && (
                            <FilterInput
                                size="small"
                                style={{ marginBottom: "0.25rem" }}
                                filterValue={searchTagQuery}
                                setFilterValue={setSearchTagQuery}
                            />
                        )}
                        <ScrollShadowWrapper className="filter-options" ref={paginatedElRef}>
                            {!tags?.length ? (
                                <span className="filter-empty-state">{t("Filters.EmptyStateTags")}</span>
                            ) : (
                                paginatedTags?.map((tag: TTags, index) => {
                                    const tagColor = tagsColors.find((item) => item.id === tag.color);
                                    return (
                                        <div
                                            className="multiple-filter-option tag"
                                            key={`tag-${index}`}
                                            onClick={() => handleTagSelection(tag)}
                                        >
                                            <FormControlLabel
                                                key={`${tag.name}-${index}`}
                                                label={tag.name}
                                                style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                                                control={<StyledCheckbox checked={chosenTags.includes(tag.name)} />}
                                            />
                                            <span
                                                className="tag-chip"
                                                style={{ backgroundColor: tagColor?.textColor }}
                                            />
                                        </div>
                                    );
                                })
                            )}
                            {!filteredTags?.length && (
                                <div style={{ paddingInline: "0.5rem", paddingBlock: "2px" }}>
                                    <SmallBody color="grey">{t("Filters.NoResultFound")}</SmallBody>
                                </div>
                            )}
                        </ScrollShadowWrapper>
                        {usesBrands && (
                            <>
                                <div className="filter-header" style={{ marginTop: "0.5rem" }}>
                                    <SmallBodyHighlight style={{ color: variables["secondary-grey"] }}>
                                        {t("Filters.FilterByBrand")}
                                    </SmallBodyHighlight>
                                    <TinyBody
                                        color="info"
                                        className="filter-reset"
                                        onClick={() => handleReset("brands")}
                                    >
                                        {t("Filters.Reset")}
                                    </TinyBody>
                                </div>
                                <ScrollShadowWrapper className="filter-options">
                                    {brands?.length === 0 ? (
                                        <span className="filter-empty-state">{t("Filters.EmptyStateBrands")}</span>
                                    ) : (
                                        brands?.map((brand: IBrand, index) => (
                                            <div className="multiple-filter-option brand" key={`brand-${index}`}>
                                                <FormControlLabel
                                                    key={`${brand.name}-${index}`}
                                                    label={brand.id === -1 ? companyName : brand.name}
                                                    control={
                                                        <Radio
                                                            checked={chosenBrand === brand.name}
                                                            onClick={() => handleBrandSelection(brand)}
                                                        />
                                                    }
                                                />
                                            </div>
                                        ))
                                    )}
                                </ScrollShadowWrapper>
                            </>
                        )}
                        {showHasTermFilter && (
                            <>
                                <div className="filter-header" style={{ marginTop: "0.5rem" }}>
                                    <SmallBodyHighlight style={{ color: variables["secondary-grey"] }}>
                                        {t("Filters.FilterByTerm")}
                                    </SmallBodyHighlight>
                                    <TinyBody
                                        color="info"
                                        className="filter-reset"
                                        onClick={() => handleReset("hasTerm")}
                                    >
                                        {t("Filters.Reset")}
                                    </TinyBody>
                                </div>
                                <ScrollShadowWrapper className="filter-options">
                                    <div className="multiple-filter-option has-term">
                                        <FormControlLabel
                                            label={t("Filters.WithoutTerm")}
                                            style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                                            control={
                                                <StyledCheckbox
                                                    checked={withoutTermOnly}
                                                    onChange={(_, checked) => setWithoutTermOnly?.(checked)}
                                                />
                                            }
                                        />
                                    </div>
                                </ScrollShadowWrapper>
                            </>
                        )}
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default MultipleFilters;
